import { EquirectangularReflectionMapping, type Scene } from 'three/webgpu';
import { HDRLoader } from 'three/addons/loaders/HDRLoader.js';

export interface EnvironmentOptions {
  /** Also show the sky as the backdrop (the hangar keeps its own cyclorama). */
  background?: boolean;
  /** Poly Haven HDRIs carry absolute radiance; each arena's sidecar tunes these down. */
  environmentIntensity?: number;
  backgroundIntensity?: number;
  /** Byte progress for the loading screen. */
  onProgress?: (event: ProgressEvent) => void;
}

/** Image-based lighting and backdrop from a CC0 HDRI (BUILD_PLAN section 4, Lighting). */
export async function loadEnvironment(
  scene: Scene,
  url: string,
  signal: AbortSignal,
  options: EnvironmentOptions = {},
): Promise<() => void> {
  const texture = await new HDRLoader().loadAsync(url, options.onProgress);
  if (signal.aborted) {
    texture.dispose();
    signal.throwIfAborted();
  }
  texture.mapping = EquirectangularReflectionMapping;
  // The lighting and the visible backdrop are pulled down to leave the key light room to shape.
  scene.environment = texture;
  scene.environmentIntensity = options.environmentIntensity ?? 0.5;
  if (options.background !== false) {
    scene.background = texture;
    scene.backgroundBlurriness = 0.12;
    scene.backgroundIntensity = options.backgroundIntensity ?? 0.55;
  }
  return () => {
    if (scene.environment === texture) scene.environment = null;
    if (scene.background === texture) scene.background = null;
    texture.dispose();
  };
}
